import React, { MouseEvent, useState } from 'react'
import classnames from 'classnames'
import { mq, useMediaQuery } from '../../utils'

import { SupportSpotlightCarousel } from '../../elements/SupportSpotlightCarousel'
import { ISlideProps } from '../../elements/SupportSpotlightCarousel'
import { SupportSpotlightCard } from 'marketing-site/src/library/elements/SupportSpotlightCard'
import { IProps as ISupportSpotlightCard } from 'marketing-site/src/library/elements/SupportSpotlightCard'

interface IProps {
  interactiveCard1?: ISupportSpotlightCard
  interactiveCard2?: ISupportSpotlightCard
  interactiveCard3?: ISupportSpotlightCard
  interactiveCard1StartPosition?: string
  isVisible?: boolean
}

export const testIds = {
  cardsContainer: 'support-spotlight-cards-container',
  firstCard: 'support-spotlight-first-card',
  secondCard: 'support-spotlight-second-card',
  thirdCard: 'support-spotlight-third-card',
  carousel: 'support-spotlight-carousel',
}

export const SupportSpotlightCards: React.FC<IProps> = ({
  interactiveCard1,
  interactiveCard2,
  interactiveCard3,
  interactiveCard1StartPosition,
  isVisible,
}: IProps) => {
  const [expandedCardId, setExpanded] = useState('1')

  const isLaptop = useMediaQuery(`(${mq.laptop})`)

  const isExpanded = (cardId: string) => {
    return cardId === expandedCardId
  }

  const handleOnCardClick = (e: MouseEvent<HTMLButtonElement>) => {
    setExpanded(e.currentTarget.dataset.cardId!)
  }

  const cardsContainerClasses = classnames('support-spotlight__cards-container', {
    [`support-spotlight__cards-container--first-start-position-${interactiveCard1StartPosition}`]:
      interactiveCard1StartPosition,
    'support-spotlight__cards-container--first-expanded': isExpanded('1'),
    'support-spotlight__cards-container--second-expanded': isExpanded('2'),
    'support-spotlight__cards-container--third-expanded': isExpanded('3'),
  })

  const renderCards = interactiveCard1 && interactiveCard2 && interactiveCard3

  const slidesArray = [interactiveCard1, interactiveCard2, interactiveCard3]

  const slides = slidesArray.map((item, index) => {
    return {
      ...item,
      id: index.toString(),
      image: item?.fallbackImageRef,
      video: item?.video,
      title: item?.title,
    } as ISlideProps
  })

  if (!renderCards) {
    return null
  }

  if (!isLaptop) {
    return (
      <div className="support-spotlight__carousel-container" data-testid={testIds.carousel}>
        <SupportSpotlightCarousel slides={slides} isVisible={isVisible} />
      </div>
    )
  }

  return (
    <div className={cardsContainerClasses} data-testid={testIds.cardsContainer}>
      <button
        className="support-card support-card__first-card"
        data-card-id="1"
        onClick={handleOnCardClick}
        data-testid={testIds.firstCard}
      >
        <SupportSpotlightCard
          {...interactiveCard1!}
          isExpanded={isExpanded('1')}
          isVisible={isVisible}
        />
      </button>
      <button
        className="support-card support-card__second-card"
        data-card-id="2"
        onClick={handleOnCardClick}
        data-testid={testIds.secondCard}
      >
        <SupportSpotlightCard
          {...interactiveCard2!}
          isExpanded={isExpanded('2')}
          isVisible={isVisible}
        />
      </button>
      <button
        className="support-card support-card__third-card"
        data-card-id="3"
        onClick={handleOnCardClick}
        data-testid={testIds.thirdCard}
      >
        <SupportSpotlightCard
          {...interactiveCard3!}
          isExpanded={isExpanded('3')}
          isVisible={isVisible}
        />
      </button>

      <style jsx>{`
        .support-spotlight__cards-container {
          --expanded-card-scale: 2.16;
          --single-grid-gap: 2.5rem;
          --double-grid-gap: calc(var(--single-grid-gap) * 2);

          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-template-rows: repeat(2, 1fr);
          grid-gap: var(--single-grid-gap);

          .support-card {
            width: 100%;
            transition: transform 400ms;
            transform-origin: top left;

            &__first-card,
            &__second-card,
            &__third-card {
              grid-column: 1 / 2;
              grid-row: 1 / 2;
            }
          }

          &--first-expanded {
            .support-card {
              &__first-card {
                transform: translate3d(0, 0, 0) scale(var(--expanded-card-scale));
              }

              &__second-card {
                transform: translate3d(calc(200% + var(--double-grid-gap)), 0, 0) scale(1);
              }

              &__third-card {
                transform: translate3d(
                    calc(200% + var(--double-grid-gap)),
                    calc(100% + var(--single-grid-gap)),
                    0
                  )
                  scale(1);
              }
            }
          }

          &--second-expanded {
            .support-card {
              &__first-card {
                transform: translate3d(0, 0, 0) scale(1);
              }

              &__second-card {
                transform: translate3d(calc(100% + var(--single-grid-gap)), 0, 0)
                  scale(var(--expanded-card-scale));
              }

              &__third-card {
                transform: translate3d(0, calc(100% + var(--single-grid-gap)), 0) scale(1);
              }
            }
          }

          &--third-expanded {
            .support-card {
              &__first-card {
                transform: translate3d(calc(200% + var(--double-grid-gap)), 0, 0) scale(1);
              }

              &__second-card {
                transform: translate3d(
                    calc(200% + var(--double-grid-gap)),
                    calc(100% + var(--single-grid-gap)),
                    0
                  )
                  scale(1);
              }

              &__third-card {
                transform: translate3d(0, 0, 0) scale(var(--expanded-card-scale));
              }
            }
          }

          &--first-start-position-right {
            .support-card {
              &__first-card,
              &__second-card,
              &__third-card {
                grid-column: 2 / 3;
                grid-row: 1 / 2;
              }
            }

            &.support-spotlight__cards-container--first-expanded {
              .support-card {
                &__first-card {
                  transform: translate3d(0, 0, 0) scale(var(--expanded-card-scale));
                }

                &__second-card {
                  transform: translate3d(calc(-100% - var(--single-grid-gap)), 0, 0) scale(1);
                }

                &__third-card {
                  transform: translate3d(
                      calc(-100% - var(--single-grid-gap)),
                      calc(100% + var(--single-grid-gap)),
                      0
                    )
                    scale(1);
                }
              }
            }

            &.support-spotlight__cards-container--second-expanded {
              .support-card {
                &__first-card {
                  transform: translate3d(calc(100% + var(--single-grid-gap)), 0, 0) scale(1);
                }

                &__second-card {
                  transform: translate3d(calc(-100% - var(--single-grid-gap)), 0, 0)
                    scale(var(--expanded-card-scale));
                }

                &__third-card {
                  transform: translate3d(
                      calc(100% + var(--single-grid-gap)),
                      calc(100% + var(--single-grid-gap)),
                      0
                    )
                    scale(1);
                }
              }
            }

            &.support-spotlight__cards-container--third-expanded {
              .support-card {
                &__first-card {
                  transform: translate3d(calc(-100% - var(--single-grid-gap)), 0, 0) scale(1);
                }

                &__second-card {
                  transform: translate3d(
                      calc(-100% - var(--single-grid-gap)),
                      calc(100% + var(--single-grid-gap)),
                      0
                    )
                    scale(1);
                }

                &__third-card {
                  transform: translate3d(0, 0, 0) scale(var(--expanded-card-scale));
                }
              }
            }
          }
        }

        @media (${mq.desktop}) {
          .support-spotlight__cards-container {
            --expanded-card-scale: 2.126;
          }
        }

        @media (${mq.desktopLg}) {
          .support-spotlight__cards-container {
            --expanded-card-scale: 2.135;
            --single-grid-gap: 3.25rem;
            --double-grid-gap: calc(var(--single-grid-gap) * 2);
          }
        }

        @media (${mq.desktopXLg}) {
          .support-spotlight__cards-container {
            --expanded-card-scale: 2.13;
          }
        }
      `}</style>
    </div>
  )
}
